import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageableRequest, PaymentRound } from 'src/app/admin-api';
import { PageContent } from 'src/app/components/table';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface BillingControlSubscriberPaymentStep {
  subscriberId?: number;
  paymentRoundId?: number;
  step?: number;
  dateCreated?: string;
  dateUpdated?: string;
  lastMessage?: string;
  creditCardPaymentId?: number;
  origin?: string;
  priority?: number;
}

export interface BillingControlCronJobInfo {
  cronJobName?: string;
  gcpProjectId?: string;
  gkeClusterName?: string;
  gkeRegion?: string;
  kubernetesNamespace?: string;
}

export interface BillingControlSubscriberPaymentStepMessageSummary {
  lastMessage?: string;
  total?: number;
}

export interface BillingControlSubscriberPaymentQueueIndicatorSummary {
  label?: string;
  keyId?: number;
  total?: number;
  totalAmount?: number;
}

export interface BillingControlSubscriberPaymentQueueItem {
  subscriberId?: number;
  paymentRoundId?: number;
  step?: number;
  dateCreated?: string;
  dateUpdated?: string;
  lastMessage?: string;
  creditCardPaymentId?: number;
  origin?: string;
  priority?: number;
  gatewayId?: number;
  gatewayName?: string;
  /** Gateway da fila (próximo); pode diferir da última tentativa. */
  queueGatewayId?: number;
  queueGatewayName?: string;
  subscriptionTypeId?: number;
  subscriptionTypeName?: string;
  subscriptionName?: string;
  amount?: number;
  subscriptionPrice?: number;
  discountValue?: number;
  installments?: number;
  recurrence?: number;
  attempt?: number;
  editionId?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  personId?: number;
  endOfCycle?: boolean;
  shippingPrice?: number;
  paymentReference?: string;
}

export interface BillingControlSubscriberPaymentQueueTabSummary {
  totalSubscribers?: number;
  totalAmount?: number;
  byGateway?: BillingControlSubscriberPaymentQueueIndicatorSummary[];
  byPlan?: BillingControlSubscriberPaymentQueueIndicatorSummary[];
  byLastMessage?: BillingControlSubscriberPaymentStepMessageSummary[];
}

@Injectable({ providedIn: 'root' })
export class BillingControlService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/billing-control/admin`;

  startPaymentRound(body?: {
    attempt?: number;
    batchSize?: number;
    onlyMonthly?: boolean;
    maxBatches?: number;
  }): Observable<string> {
    return this.http
      .post<
        ResponseB4A<{
          totalEnqueued?: number;
          batchesExecuted?: number;
          remainingEligibleCount?: number;
          lastPaymentRoundId?: number;
          attempt?: number;
        }>
      >(`${this.baseUrl}/start-payment-round`, body ?? {}, {
        headers: this.authHeaders()
      })
      .pipe(
        map((response) => {
          const result = response.result;
          if (!result) {
            return 'StartPaymentRound executado.';
          }
          return `Lote enfileirado: attempt=${result.attempt ?? 1}, enqueued=${result.totalEnqueued ?? 0}, batches=${result.batchesExecuted ?? 0}, remaining=${result.remainingEligibleCount ?? 0}, round=${result.lastPaymentRoundId ?? '-'}`;
        })
      );
  }

  createPaymentRound(dateScheduled: string | null): Observable<PaymentRound> {
    return this.http
      .post<ResponseB4A<PaymentRound>>(`${this.baseUrl}/payment-round`, { dateScheduled }, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result));
  }

  listPaymentRounds(): Observable<PaymentRound[]> {
    return this.http
      .get<ResponseB4A<PaymentRound[]>>(`${this.baseUrl}/payment-rounds`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result ?? []));
  }

  getCronJobInfo(cronJobName: string): Observable<BillingControlCronJobInfo> {
    return this.http
      .get<ResponseB4A<BillingControlCronJobInfo>>(
        `${this.baseUrl}/cron-jobs/${encodeURIComponent(cronJobName)}/info`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  triggerCronJob(cronJobName: string): Observable<string> {
    return this.http
      .post<ResponseB4A<string>>(
        `${this.baseUrl}/cron-jobs/${encodeURIComponent(cronJobName)}/trigger`,
        {},
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  deletePaymentRound(paymentRoundId: number): Observable<string> {
    return this.http
      .delete<ResponseB4A<string>>(`${this.baseUrl}/payment-round/${paymentRoundId}`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result));
  }

  listSubscriberPaymentQueue(
    step: number,
    request: PageableRequest
  ): Observable<PageContent<BillingControlSubscriberPaymentQueueItem>> {
    const params = new HttpParams().set('step', step);
    return this.http
      .put<ResponseB4A<PageContent<BillingControlSubscriberPaymentQueueItem>>>(
        `${this.baseUrl}/subscriber-payment-queue`,
        request,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result as PageContent<BillingControlSubscriberPaymentQueueItem>));
  }

  summarizeSubscriberPaymentQueue(
    step: number,
    paymentRoundId?: number | null
  ): Observable<BillingControlSubscriberPaymentQueueTabSummary> {
    let params = new HttpParams().set('step', step);
    if (paymentRoundId != null) {
      params = params.set('paymentRoundId', paymentRoundId);
    }
    return this.http
      .get<ResponseB4A<BillingControlSubscriberPaymentQueueTabSummary>>(
        `${this.baseUrl}/subscriber-payment-queue/summary`,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result ?? {}));
  }

  listSubscriberPaymentSteps(
    step: number,
    request: PageableRequest
  ): Observable<PageContent<BillingControlSubscriberPaymentStep>> {
    const params = new HttpParams().set('step', step);
    return this.http
      .put<ResponseB4A<PageContent<BillingControlSubscriberPaymentStep>>>(
        `${this.baseUrl}/subscriber-payment-steps`,
        request,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result as PageContent<BillingControlSubscriberPaymentStep>));
  }

  summarizeSubscriberPaymentStepsByLastMessage(
    step: number,
    paymentRoundId?: number | null
  ): Observable<BillingControlSubscriberPaymentStepMessageSummary[]> {
    let params = new HttpParams().set('step', step);
    if (paymentRoundId != null) {
      params = params.set('paymentRoundId', paymentRoundId);
    }
    return this.http
      .get<ResponseB4A<BillingControlSubscriberPaymentStepMessageSummary[]>>(
        `${this.baseUrl}/subscriber-payment-steps/summary`,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result ?? []));
  }

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }
}
